'use client';

import { useEffect, useMemo, useRef, type ComponentProps } from 'react';
import { type VariantProps, cva } from 'class-variance-authority';

import { ReactShaderToy } from '@/components/react-shader-toy';
import { useAgentAudioVisualizerWave, type WaveAgentState } from '@/hooks/use-agent-audio-visualizer-wave';
import { cn } from '@/lib/utils';

const DEFAULT_COLOR = '#1FD5F9';

function hexToRgb(hexColor: string) {
  try {
    const rgbColor = hexColor.match(/^#([0-9a-fA-F]{2})([0-9a-fA-F]{2})([0-9a-fA-F]{2})$/);

    if (rgbColor) {
      const [, r, g, b] = rgbColor;
      const color = [r, g, b].map((c = '00') => parseInt(c, 16) / 255);

      return color;
    }
  } catch {
    console.error(
      `Invalid hex color '${hexColor}'.\nFalling back to default color '${DEFAULT_COLOR}'.`,
    );
  }

  return hexToRgb(DEFAULT_COLOR);
}

const shaderSource = `
const float TAU = 6.28318530718;

// Noise for dithering
vec2 randFibo(vec2 p) {
  p = fract(p * vec2(443.897, 441.423));
  p += dot(p, p.yx + 19.19);
  return fract((p.xx + p.yx) * p.xy);
}

// Luma for alpha
float luma(vec3 color) {
  return dot(color, vec3(0.299, 0.587, 0.114));
}

// RGB to HSV
vec3 rgb2hsv(vec3 c) {
  vec4 K = vec4(0.0, -1.0 / 3.0, 2.0 / 3.0, -1.0);
  vec4 p = mix(vec4(c.bg, K.wz), vec4(c.gb, K.xy), step(c.b, c.g));
  vec4 q = mix(vec4(p.xyw, c.r), vec4(c.r, p.yzx), step(p.x, c.r));
  float d = q.x - min(q.w, q.y);
  float e = 1.0e-10;
  return vec3(abs(q.z + (q.w - q.y) / (6.0 * d + e)), d / (q.x + e), q.x);
}

// HSV to RGB
vec3 hsv2rgb(vec3 c) {
  vec4 K = vec4(1.0, 2.0 / 3.0, 1.0 / 3.0, 3.0);
  vec3 p = abs(fract(c.xxx + K.xyz) * 6.0 - K.www);
  return c.z * mix(K.xxx, clamp(p - K.xxx, 0.0, 1.0), c.y);
}

// Bell curve function for attenuation from center with rounded top
float bellCurve(float distanceFromCenter, float maxDistance) {
  float normalizedDistance = distanceFromCenter / maxDistance;
  // Use cosine with high power for smooth rounded top
  return pow(cos(normalizedDistance * (3.14159265359 / 4.0)), 16.0);
}

// Calculate the sine wave
float oscilloscopeWave(float x, float centerX, float time) {
  float relativeX = x - centerX;
  float maxDistance = centerX;
  float distanceFromCenter = abs(relativeX);
  
  // Apply bell curve for amplitude attenuation
  float bell = bellCurve(distanceFromCenter, maxDistance);
  
  // Calculate wave with uniforms and bell curve attenuation
  float wave = sin(relativeX * uFrequency + time * uSpeed) * uAmplitude * bell;
  
  return wave;
}

void mainImage(out vec4 fragColor, in vec2 fragCoord) {
  vec2 uv = fragCoord / iResolution.xy;
  vec2 pos = uv - 0.5;
  
  // Calculate center and positions
  float centerX = 0.5;
  float centerY = 0.5;
  float x = uv.x;
  float y = uv.y;
  
  // Convert line width from pixels to UV space
  // Use the average of width and height to handle aspect ratio
  float pixelSize = 2.0 / (iResolution.x + iResolution.y);
  float lineWidthUV = uLineWidth * pixelSize;
  float smoothingUV = uSmoothing * pixelSize;
  
  // Find minimum distance to the wave by sampling nearby points
  // This gives us consistent line width without high-frequency artifacts
  const int NUM_SAMPLES = 24; // Must be const for GLSL loop
  float minDist = 1000.0;
  float sampleRange = 0.02; // Range to search for closest point
  
  for(int i = 0; i < NUM_SAMPLES; i++) {
    float offset = (float(i) / float(NUM_SAMPLES - 1) - 0.5) * sampleRange;
    float sampleX = x + offset;
    float waveY = centerY + oscilloscopeWave(sampleX, centerX, iTime);
    
    // Calculate distance from current pixel to this point on the wave
    vec2 wavePoint = vec2(sampleX, waveY);
    vec2 currentPoint = vec2(x, y);
    float dist = distance(currentPoint, wavePoint);
    
    minDist = min(minDist, dist);
  }
  
  // Solid line with smooth edges using minimum distance
  float line = smoothstep(lineWidthUV + smoothingUV, lineWidthUV - smoothingUV, minDist);
  
  vec3 color = uColor;
  if(abs(uColorShift) > 0.01) {
    // Keep the center 50% at base color, then ramp shift across outer 25% on each side.
    float centerBandHalfWidth = 0.2;
    float edgeBandWidth = 0.5;
    float distanceFromCenter = abs(x - centerX);
    float edgeFactor = clamp((distanceFromCenter - centerBandHalfWidth) / edgeBandWidth, 0.0, 1.0);
    vec3 hsv = rgb2hsv(color);
    // Hue shift is zero in the center band and strongest at far edges.
    hsv.x = fract(hsv.x + edgeFactor * uColorShift * 0.3);
    color = hsv2rgb(hsv);
  }
  
  // Apply line intensity
  color *= line;
  
  // Add dithering for smoother gradients
  // color += (randFibo(fragCoord).x - 0.5) / 255.0;
  
  // Calculate alpha based on line intensity
  float alpha = line * uMix;
  
  fragColor = vec4(color * uMix, alpha);
}`;

interface WaveShaderProps {
  className?: string;
  color?: `#${string}`;
  colorShift?: number;
  lineWidth?: number;
  blur?: number;
  /** Stable uniform ref — mutated in-place by the parent, read each GL frame. */
  uniformsRef: React.RefObject<Record<string, { type: string; value: number | number[] }>>;
}

function WaveShader({
  color = '#1FD5F9',
  colorShift = 0.05,
  lineWidth = 2.0,
  blur = 0.5,
  uniformsRef,
  ref,
  className,
  ...props
}: WaveShaderProps & ComponentProps<'div'>) {
  const rgbColor = useMemo(() => hexToRgb(color), [color]);

  // Keep static uniforms in sync without re-creating the object
  useEffect(() => {
    const u = uniformsRef.current;
    if (!u) return;
    u.uLineWidth.value = lineWidth;
    u.uSmoothing.value = blur;
    u.uColor.value = rgbColor ?? [0, 0.7, 1];
    u.uColorShift.value = colorShift;
  }, [uniformsRef, rgbColor, colorShift, lineWidth, blur]);

  return (
    <div ref={ref} className={className} {...props}>
      <ReactShaderToy
        fs={shaderSource}
        devicePixelRatio={Math.min(globalThis.devicePixelRatio ?? 1, 1.5)}
        uniforms={uniformsRef.current}
        style={{ width: '100%', height: '100%' }}
      />
    </div>
  );
}

WaveShader.displayName = 'WaveShader';

export const AgentAudioVisualizerWaveVariants = cva(['aspect-square'], {
  variants: {
    size: {
      icon: 'h-[24px]',
      sm: 'h-[56px]',
      md: 'h-[112px]',
      lg: 'h-[224px]',
      xl: 'h-[448px]',
    },
  },
  defaultVariants: {
    size: 'lg',
  },
});

export interface AgentAudioVisualizerWaveProps {
  /**
   * The size of the visualizer.
   * @defaultValue 'lg'
   */
  size?: 'icon' | 'sm' | 'md' | 'lg' | 'xl';
  /**
   * The agent state.
   * @defaultValue 'speaking'
   */
  state?: WaveAgentState;
  /**
   * The color of the wave in hexidecimal format.
   * @defaultValue '#1FD5F9'
   */
  color?: `#${string}`;
  /**
   * The color shift of the wave. Higher values increase hue variation toward the edges.
   * @defaultValue 0.05
   */
  colorShift?: number;
  /**
   * The line width of the wave in pixels.
   * @defaultValue 2.0
   */
  lineWidth?: number;
  /**
   * The blur of the wave in pixels.
   * @defaultValue 0.5
   */
  blur?: number;
  /**
   * Additional CSS class names to apply to the container.
   */
  className?: string;
}

/**
 * A wave-style audio visualizer that responds to agent state and audio levels.
 * Displays an animated wave that reacts to the current agent state (connecting, thinking, speaking, etc.)
 * and audio volume when speaking.
 *
 * @extends ComponentProps<'div'>
 *
 * @example ```tsx
 * <AgentAudioVisualizerWave
 *   size="lg"
 *   state="speaking"
 *   color="#1FD5F9"
 *   colorShift={0.3}
 *   lineWidth={2}
 *   blur={0.5}
 *   audioTrack={audioTrack}
 * />
 * ```
 */
export function AgentAudioVisualizerWave({
  size = 'lg',
  state = 'speaking',
  color,
  colorShift = 0.05,
  lineWidth,
  blur,
  className,
  ref,
  ...props
}: AgentAudioVisualizerWaveProps &
  ComponentProps<'div'> &
  VariantProps<typeof AgentAudioVisualizerWaveVariants>) {
  const _lineWidth = useMemo(() => {
    if (lineWidth !== undefined) {
      return lineWidth;
    }
    switch (size) {
      case 'icon':
      case 'sm':
        return 2;
      default:
        return 1;
    }
  }, [lineWidth, size]);

  const { speed, amplitude, frequency, opacity } = useAgentAudioVisualizerWave({
    state,
  });

  // Stable uniform object — never re-created, just mutated.
  // ReactShaderToy reads .value from this ref each GL frame.
  const uniformsRef = useRef({
    uSpeed: { type: '1f' as const, value: speed.get() },
    uAmplitude: { type: '1f' as const, value: amplitude.get() },
    uFrequency: { type: '1f' as const, value: frequency.get() },
    uMix: { type: '1f' as const, value: opacity.get() },
    uLineWidth: { type: '1f' as const, value: _lineWidth },
    uSmoothing: { type: '1f' as const, value: 0.5 },
    uColor: { type: '3fv' as const, value: [0, 0.7, 1] as number[] },
    uColorShift: { type: '1f' as const, value: colorShift },
  });

  // Subscribe to motion values and push directly into the uniform ref.
  // These subscriptions fire outside of React's render cycle.
  useEffect(() => {
    const unsubs = [
      speed.on('change', (v) => { uniformsRef.current.uSpeed.value = v; }),
      amplitude.on('change', (v) => { uniformsRef.current.uAmplitude.value = v; }),
      frequency.on('change', (v) => { uniformsRef.current.uFrequency.value = v; }),
      opacity.on('change', (v) => { uniformsRef.current.uMix.value = v; }),
    ];
    return () => { for (const u of unsubs) u(); };
  }, [speed, amplitude, frequency, opacity]);

  return (
    <WaveShader
      ref={ref}
      data-lk-state={state}
      color={color}
      colorShift={colorShift}
      lineWidth={_lineWidth}
      blur={blur}
      uniformsRef={uniformsRef}
      className={cn(
        AgentAudioVisualizerWaveVariants({ size }),
        'mask-[linear-gradient(90deg,transparent_0%,black_20%,black_80%,transparent_100%)]',
        className,
      )}
      {...props}
    />
  );
}
